import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-responsive-dt";
import "datatables.net-select-dt";
import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import {
  getAllUsers,
  updateStatusUser,
  updateRole,
  deleteUser,
} from "../../../redux/userSlice/userSlice";
import notify from "../../../hooks/useNotifaction";
import { ROLES } from "../../../constants/roles";
import { AuthContext } from "../../../context/AuthContext";
import "./User.css";

DataTable.use(DT);

export default function User() {
  const [users, setUsers] = useState([]);
  const [allUsersData, setAllUsersData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [tableKey, setTableKey] = useState(0);
  const dispatch = useDispatch();
  const { allUsers, status, pagination } = useSelector((state) => state.user);
  const table = useRef();
  const [loading, setLoading] = useState(true);
  const { user, role } = useContext(AuthContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState("");

  // جلب كل المستخدمين من كل الصفحات
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        setLoading(true);

        // جلب أول صفحة
        const firstPage = await dispatch(
          getAllUsers({ page: 1, status: true })
        ).unwrap();
        
        let allData = [...(firstPage.data || [])];
        const totalPagesCount = firstPage.pagination
          ? Math.ceil(firstPage.pagination.total / firstPage.pagination.limit)
          : 1;

        setTotalPages(totalPagesCount);

        // جلب باقي الصفحات
        if (totalPagesCount > 1) {
          const promises = [];
          for (let page = 2; page <= totalPagesCount; page++) {
            promises.push(
              dispatch(getAllUsers({ page, status: true })).unwrap()
            );
          }

          const otherPages = await Promise.all(promises);
          otherPages.forEach((pageData) => {
            if (pageData.data) {
              allData = [...allData, ...pageData.data];
            }
          });
        }

        // فلترة المستخدمين (إزالة المستخدم الحالي والـ OWNER)
        const filteredUser = allData.filter(
          (uu) => uu.id !== user.id && uu.role !== ROLES.OWNER
        );

        console.log(`✅ Loaded all ${filteredUser.length} users`);
        setAllUsersData(filteredUser);
        setUsers(filteredUser);
      } catch (error) {
        console.error("❌ Error loading users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, [dispatch, user.id]);

  // البحث المحلي في الـ Frontend
  const searchInFrontend = useCallback((keyword, data) => {
    if (!keyword.trim()) {
      return data;
    }

    const searchTerm = keyword.toLowerCase().trim();

    return data.filter((u) => {
      const name = u.name?.toLowerCase() || "";
      const email = u.email?.toLowerCase() || "";
      const phone = u.phone?.toLowerCase() || "";
      const roleText = u.role?.toLowerCase() || "";
      const statusText = u.status?.toLowerCase() || "";

      return (
        name.includes(searchTerm) ||
        email.includes(searchTerm) ||
        phone.includes(searchTerm) ||
        roleText.includes(searchTerm) ||
        statusText.includes(searchTerm)
      );
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((keyword) => {
      console.log("🔍 Frontend Search for:", keyword);

      if (keyword.trim() !== "") {
        setIsSearching(true);
        const results = searchInFrontend(keyword, allUsersData);
        console.log(`✅ Found ${results.length} results`);
        setUsers(results);
        setTableKey((prev) => prev + 1);
      } else {
        setIsSearching(false);
        setUsers(allUsersData);
        setTableKey((prev) => prev + 1);
      }
    }, 300),
    [allUsersData, searchInFrontend]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery("");
    setIsSearching(false);
    setUsers(allUsersData);
    setTableKey((prev) => prev + 1);
  };

  const handleAccept = async (userId, userRole) => {
    try {
      setLoading(true);
      await dispatch(
        updateStatusUser({ id: userId, data: { status: "active" } })
      );
      await dispatch(updateRole({ id: userId, data: { role: userRole } }));
      
      // إعادة جلب كل البيانات
      const firstPage = await dispatch(
        getAllUsers({ page: 1, status: true })
      ).unwrap();
      
      let allData = [...(firstPage.data || [])];
      const totalPagesCount = firstPage.pagination
        ? Math.ceil(firstPage.pagination.total / firstPage.pagination.limit)
        : 1;

      if (totalPagesCount > 1) {
        const promises = [];
        for (let page = 2; page <= totalPagesCount; page++) {
          promises.push(
            dispatch(getAllUsers({ page, status: true })).unwrap()
          );
        }
        const otherPages = await Promise.all(promises);
        otherPages.forEach((pageData) => {
          if (pageData.data) {
            allData = [...allData, ...pageData.data];
          }
        });
      }

      const filteredUser = allData.filter(
        (uu) => uu.id !== user.id && uu.role !== ROLES.OWNER
      );
      
      setAllUsersData(filteredUser);
      
      if (isSearching) {
        const results = searchInFrontend(searchQuery, filteredUser);
        setUsers(results);
      } else {
        setUsers(filteredUser);
      }
      
      setTableKey((prev) => prev + 1);
      notify("تم قبول المستخدم وتعيين الصلاحية", "success");
    } catch (err) {
      console.error(err);
      notify("حدث خطأ أثناء قبول المستخدم", "error");
    }
    setLoading(false);
  };

  const handleUpdateRole = async (userId, userRole) => {
    try {
      setLoading(true);
      await dispatch(updateRole({ id: userId, data: { role: userRole } }));
      
      // تحديث محلي
      const updatedData = allUsersData.map((u) =>
        u.id === userId ? { ...u, role: userRole } : u
      );
      setAllUsersData(updatedData);
      
      if (isSearching) {
        const results = searchInFrontend(searchQuery, updatedData);
        setUsers(results);
      } else {
        setUsers(updatedData);
      }
      
      setTableKey((prev) => prev + 1);
      notify("تم تعديل الصلاحية بنجاح", "success");
    } catch (err) {
      console.error(err);
      notify("حدث خطأ أثناء تعديل الدور", "error");
    }
    setLoading(false);
  };

  const handleDeleteUser = (userId) => {
    console.log(userId);
    Swal.fire({
      title: "هل أنت متأكد؟",
      text: "لا يمكنك التراجع بعد الحذف!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "نعم، احذفه",
      cancelButtonText: "إلغاء",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading(true);
          await dispatch(deleteUser(userId));
          
          // تحديث البيانات بعد الحذف
          const updatedData = allUsersData.filter((u) => u.id !== userId);
          setAllUsersData(updatedData);
          
          if (isSearching) {
            const results = searchInFrontend(searchQuery, updatedData);
            setUsers(results);
          } else {
            setUsers(updatedData);
          }
          
          setTableKey((prev) => prev + 1);
          notify("تم حذف المستخدم", "success");
        } catch (err) {
          console.error(err);
          notify("حدث خطأ أثناء الحذف", "error");
        }
        setLoading(false);
      }
    });
  };

  const columns = [
    { title: "الاسم", data: "name" },
    { title: "البريد الإلكتروني", data: "email" },
    { title: "رقم الهاتف", data: "phone" },
    { title: "الدور", data: "role" },
    { title: "الحالة", data: "status" },
    {
      title: "تاريخ الإنشاء",
      data: "createdAt",
      render: (data) => format(new Date(data), "yyyy-MM-dd"),
    },
    {
      title: "الإجراءات",
      data: null,
      createdCell: (td, cellData, rowData) => {
        const roles = Object.values(ROLES);
        const filteredRoles =
          role === ROLES.OWNER
            ? roles
            : roles.filter((r) => r !== ROLES.OWNER);

        const selectHtml = `
    <select class="form-select role-select" data-id="${
      rowData.id
    }" style="width: 120px; display: inline-block;">
      <option value="">اختر الصلاحية</option>
      ${filteredRoles
        .map(
          (r) =>
            `<option value="${r}" ${
              rowData.role === r ? "selected" : ""
            }>${r}</option>`
        )
        .join("")}
    </select>
  `;

        const acceptBtn =
          rowData.status === "pending"
            ? `<button class="btn btn-success accept-btn" data-id="${rowData.id}" style="margin-right: 5px;">قبول</button>`
            : "";

        const updateRoleBtn =
          rowData.status !== "pending"
            ? `<button class="btn btn-primary update-role-btn" data-id="${rowData.id}" style="margin-right: 5px;">تعديل الصلاحية</button>`
            : "";

        const deleteBtn = `<button class="btn btn-danger delete-user-btn" data-id="${rowData.id}">حذف</button>`;

        td.innerHTML = `${acceptBtn} ${selectHtml} ${updateRoleBtn} ${deleteBtn}`;
      },
    },
  ];

  useEffect(() => {
    const handleClick = (event) => {
      const acceptBtn = event.target.closest(".accept-btn");
      const updateBtn = event.target.closest(".update-role-btn");
      const deleteBtn = event.target.closest(".delete-user-btn");

      if (acceptBtn) {
        const userId = acceptBtn.getAttribute("data-id");
        const selectElement = acceptBtn
          .closest("td")
          .querySelector(".role-select");
        const selectedRole = selectElement?.value;
        if (selectedRole) {
          handleAccept(userId, selectedRole);
        } else {
          notify("يرجى اختيار صلاحية قبل القبول", "warning");
        }
      }

      if (updateBtn) {
        const userId = updateBtn.getAttribute("data-id");
        const selectElement = updateBtn
          .closest("td")
          .querySelector(".role-select");
        const selectedRole = selectElement?.value;
        if (selectedRole) {
          handleUpdateRole(userId, selectedRole);
        } else {
          notify("يرجى اختيار صلاحية", "warning");
        }
      }

      if (deleteBtn) {
        const userId = deleteBtn.getAttribute("data-id");
        handleDeleteUser(userId);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isSearching, searchQuery, allUsersData]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="main">
      <div>
        <h1 className="text-white text-center">المستخدمين</h1>
      </div>
      <div className="container-fluid">
        <div className="row justify-content-between align-items-center mt-3 mb-4">
          {/* عدد المستخدمين */}
          <div className="col-md-4 d-flex justify-content-md-start justify-content-center mb-3">
            <div className="card py-2 px-3">
              <p className="mb-0 primary-100">
                {isSearching ? (
                  <>
                    نتائج البحث:{" "}
                    <span className="primary-100">{users.length}</span>
                    <small
                      className="text-muted d-block"
                      style={{ fontSize: "0.8rem" }}
                    >
                      (من إجمالي {allUsersData.length})
                    </small>
                  </>
                ) : (
                  <>
                    عدد المستخدمين:{" "}
                    <span className="primary-100">{allUsersData.length}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Search Box */}
          <div className="col-md-5 mb-3">
            <div className="search-box-users position-relative">
              <input
                type="text"
                placeholder="ابحث بالاسم، البريد، الهاتف، الدور، الحالة..."
                className="form-control search-input-users"
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <i className="fas fa-search search-icon-users"></i>
              {searchQuery && (
                <button
                  className="btn-clear-search-users"
                  onClick={clearSearch}
                  title="مسح البحث"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
            {isSearching && (
              <div className="search-info-users text-center mt-2">
                <small className="text-success">
                  <i className="fas fa-check-circle me-1"></i>
                  وجد {users.length} نتيجة للبحث عن "{searchQuery}"
                </small>
              </div>
            )}
          </div>

          {/* مساحة فارغة */}
          <div className="col-md-3 mb-3"></div>
        </div>

        {/* Loading Spinner */}
        {loading ? (
          <div className="loading-spinner-users">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
            <p className="text-center mt-3 text-muted">
              جاري تحميل المستخدمين...
            </p>
          </div>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowX: "auto" }}>
              {users && users.length > 0 ? (
                <DataTable
                  key={tableKey}
                  options={{
                    responsive: true,
                    paging: false,
                    searching: false,
                    info: false,
                    destroy: true,
                  }}
                  data={users}
                  columns={columns}
                  className="display table table-striped table-bordered"
                  ref={table}
                />
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-users fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {searchQuery
                      ? `لا توجد نتائج للبحث عن "${searchQuery}"`
                      : "لا يوجد مستخدمين"}
                  </p>
                  {searchQuery && (
                    <button
                      className="btn btn-sm btn-primary mt-2"
                      onClick={clearSearch}
                    >
                      <i className="fas fa-times me-2"></i>
                      مسح البحث وعرض كل المستخدمين
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Results Summary */}
            {users.length > 0 && (
              <div className="results-summary-users text-center mt-3">
                <p className="text-muted">
                  عرض {users.length} مستخدم
                  {isSearching && ` من إجمالي ${allUsersData.length}`}
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}